import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { assertTranslationConsistency, LanguageCode, translations, TranslationKey } from '../i18n/translations';

type LanguageContextType = {
    language: LanguageCode;
    setLanguage: (lang: LanguageCode) => void;
    t: (key: TranslationKey) => string;
};

const LANGUAGE_STORAGE_KEY = 'dashboard_language';

const getInitialLanguage = (): LanguageCode => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved === 'it' || saved === 'en' || saved === 'fr' || saved === 'de') return saved;
    return 'it';
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
assertTranslationConsistency();

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
    const [language, setLanguageState] = useState<LanguageCode>(getInitialLanguage);

    const setLanguage = (lang: LanguageCode) => {
        setLanguageState(lang);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    };

    const value = useMemo(() => ({
        language,
        setLanguage,
        t: (key: TranslationKey) => translations[language][key] ?? translations.it[key] ?? key,
    }), [language]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) throw new Error('useLanguage must be used within LanguageProvider');
    return context;
};
